import React from 'react';
import { Unlock, Keyboard, Globe, Radio, Edit, Trophy, Target, Tent, Map, BookOpen } from 'lucide-react';
import { LEARNING_ORDER } from '../../constants';
import { COUNTRIES } from '../../data/qsoData';
import { LITERATURE } from '../../data/literature';
import CollapsibleSection from '../CollapsibleSection';

const MenuGameModes = ({
    gameMode,
    setGameMode,
    progress,
    setLevel,
    callSignRegion,
    setCallSignRegion,
    activityType,
    setActivityType,
    activityRole,
    setActivityRole,
    contestType,
    setContestType,
    qsoRole,
    setQsoRole,
    userSettings,
    setView,
    pileupCount,
    setPileupCount,
    customChars,
    setCustomChars,
    comprehensionMode,
    setComprehensionMode,
    inputMode, // Added to detect receive-only mode
    startGame, // Added to allow immediate start from sub-mode buttons
    setCurrentLiteratureId,
    setCurrentSegmentIndex,
    quizInputMode,
    setQuizInputMode
}) => {
    // Determine user's skill level (default to 1 if not set)
    // Ensure it's a number by parsing (in case it's stored as string)
    const parsedLevel = parseInt(userSettings?.skill_level);
    const userSkillLevel = !isNaN(parsedLevel) ? parsedLevel : 1;

    return (
        <>
            <h4 className="text-xs font-bold text-muted uppercase tracking-wider mb-4">Training Mode</h4>

            <div className="space-y-4">
                {/* SECTION 1: LEARNING */}
                <CollapsibleSection
                    title="Learning"
                    level={1}
                    isCurrentLevel={userSkillLevel === 1}
                    isNextLevel={userSkillLevel === 0} // Never true, but for consistency
                    defaultExpanded={userSkillLevel === 1}
                    onExpand={() => setGameMode('guided')}
                >
                    <div className="space-y-2">
                        {/* 1. Koch Method */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'guided' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button onClick={() => setGameMode('guided')} className={`w-full p-3 flex items-center justify-between ${gameMode === 'guided' ? 'text-primary' : 'text-muted'}`}>
                                <div className="flex items-center gap-2">
                                    <Unlock size={16} />
                                    <div className="text-left">
                                        <span className="font-bold block">Guided Lessons (Koch)</span>
                                        <span className="text-[10px] text-muted">Progressive character unlocking</span>
                                    </div>
                                </div>
                                {gameMode === 'guided' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'guided' && (
                                <div className="p-3 border-t border-primary/20 space-y-2">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-muted">Current Level</span>
                                        <span className="text-primary font-bold">{progress.level} / {LEARNING_ORDER.length}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="2"
                                        max={Math.max(2, progress.maxLevel || 2)}
                                        value={progress.level}
                                        onChange={(e) => setLevel(parseInt(e.target.value))}
                                        className="w-full h-2 bg-surface-hover rounded-lg appearance-none cursor-pointer accent-primary"
                                    />
                                    <div className="flex justify-between text-[10px] text-muted">
                                        <span>Start (K, M)</span>
                                        <span>Max Unlocked: {progress.maxLevel || progress.level}</span>
                                    </div>
                                    <div className="text-center text-xs text-muted mt-1">
                                        Unlocks: <span className="font-bold text-primary">{LEARNING_ORDER[progress.level - 1]}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 2. Character Sets */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${['letters', 'numbers', 'special', 'mixed'].includes(gameMode) ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('letters')}
                                className={`w-full p-3 flex items-center justify-between ${['letters', 'numbers', 'special', 'mixed'].includes(gameMode) ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Keyboard size={16} />
                                    <span className="font-bold">Character Sets</span>
                                </div>
                                {['letters', 'numbers', 'special', 'mixed'].includes(gameMode) && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>

                            {['letters', 'numbers', 'special', 'mixed'].includes(gameMode) && (
                                <div className="grid grid-cols-4 gap-2 p-2 border-t border-primary/20">
                                    <button onClick={() => setGameMode('letters')} className={`p-2 rounded-lg text-sm border border-border transition-colors ${gameMode === 'letters' ? 'bg-primary text-primary-content font-bold' : 'bg-surface text-muted hover:bg-surface-hover'}`}>A-Z</button>
                                    <button onClick={() => setGameMode('numbers')} className={`p-2 rounded-lg text-sm border border-border transition-colors ${gameMode === 'numbers' ? 'bg-primary text-primary-content font-bold' : 'bg-surface text-muted hover:bg-surface-hover'}`}>0-9</button>
                                    <button onClick={() => setGameMode('special')} className={`p-2 rounded-lg text-sm border border-border transition-colors ${gameMode === 'special' ? 'bg-primary text-primary-content font-bold' : 'bg-surface text-muted hover:bg-surface-hover'}`}>? ! @</button>
                                    <button onClick={() => setGameMode('mixed')} className={`p-2 rounded-lg text-sm border border-border transition-colors ${gameMode === 'mixed' ? 'bg-primary text-primary-content font-bold' : 'bg-surface text-muted hover:bg-surface-hover'}`}>Mix</button>
                                </div>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>

                {/* SECTION 2: IMPROVING */}
                <CollapsibleSection
                    title="Improving"
                    level={2}
                    isCurrentLevel={userSkillLevel === 2}
                    isNextLevel={userSkillLevel === 1}
                    defaultExpanded={userSkillLevel === 2}
                    onExpand={() => setGameMode('callsigns')}
                >
                    <div className="space-y-2">
                        {/* 3. Call Signs and Codes */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${['callsigns', 'qcodes', 'othercodes'].includes(gameMode) ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('callsigns')}
                                className={`w-full p-3 flex items-center justify-between ${['callsigns', 'qcodes', 'othercodes'].includes(gameMode) ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Globe size={16} /> <span className="font-bold">Call Signs and Codes</span></div>
                                {['callsigns', 'qcodes', 'othercodes'].includes(gameMode) && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {['callsigns', 'qcodes', 'othercodes'].includes(gameMode) && (
                                <div className="flex flex-col border-t border-primary/20">
                                    <div className="flex divide-x divide-primary/20 border-b border-primary/20">
                                        <button onClick={() => setGameMode('callsigns')} className={`flex-1 py-2 text-xs font-bold ${gameMode === 'callsigns' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}>Call Signs</button>
                                        <button onClick={() => setGameMode('qcodes')} className={`flex-1 py-2 text-xs font-bold ${gameMode === 'qcodes' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}>Q-Codes</button>
                                        <button onClick={() => setGameMode('othercodes')} className={`flex-1 py-2 text-xs font-bold ${gameMode === 'othercodes' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}>Other Codes</button>
                                    </div>
                                    {gameMode === 'callsigns' && (
                                        <div className="flex divide-x divide-primary/20">
                                            <button
                                                onClick={() => setCallSignRegion(userSettings.country)}
                                                className={`flex-1 py-2 text-xs font-bold truncate px-2 ${callSignRegion === userSettings.country ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                            >
                                                {COUNTRIES.find(c => c.code === userSettings.country)?.flag} {userSettings.country}
                                            </button>
                                            <button
                                                onClick={() => setCallSignRegion('WORLD')}
                                                className={`flex-1 py-2 text-xs font-bold ${callSignRegion === 'WORLD' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                            >
                                                🌍 World
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 4. Activities */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'activities' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('activities')}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'activities' ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Radio size={16} /> <span className="font-bold">Activities</span></div>
                                {gameMode === 'activities' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'activities' && (
                                <div className="space-y-1 border-t border-primary/20 p-2">
                                    <div className="flex divide-x divide-primary/20 border border-primary/20 rounded overflow-hidden">
                                        <button
                                            onClick={() => setActivityType('POTA')}
                                            className={`flex-1 py-1 text-xs font-bold ${activityType === 'POTA' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            🏞️ POTA
                                        </button>
                                        <button
                                            onClick={() => setActivityType('SOTA')}
                                            className={`flex-1 py-1 text-xs font-bold ${activityType === 'SOTA' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            🏔️ SOTA
                                        </button>
                                        <button
                                            onClick={() => setActivityType('WWFF')}
                                            className={`flex-1 py-1 text-xs font-bold ${activityType === 'WWFF' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            🌳 WWFF
                                        </button>
                                    </div>
                                    <div className="flex divide-x divide-primary/20 border border-primary/20 rounded overflow-hidden">
                                        <button
                                            onClick={() => setActivityRole('HUNTER')}
                                            className={`flex-1 py-1 text-xs font-bold ${activityRole === 'HUNTER' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            <Target size={10} className="inline mr-1" /> Hunter
                                        </button>
                                        <button
                                            onClick={() => setActivityRole('ACTIVATOR')}
                                            className={`flex-1 py-1 text-xs font-bold ${activityRole === 'ACTIVATOR' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            <Tent size={10} className="inline mr-1" /> Activator
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Custom Practice */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'custom' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('custom')}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'custom' ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Edit size={16} /> <span className="font-bold">Custom Practice</span></div>
                                {gameMode === 'custom' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'custom' && (
                                <div className="p-3 border-t border-primary/20 space-y-3">
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => setCustomChars('A B C D E F G H I J K L M N O P Q R S T U V W X Y Z')}
                                            className="px-2 py-1.5 text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20 rounded border border-primary/20 transition-colors"
                                        >
                                            Letters
                                        </button>
                                        <button
                                            onClick={() => setCustomChars('0 1 2 3 4 5 6 7 8 9')}
                                            className="px-2 py-1.5 text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20 rounded border border-primary/20 transition-colors"
                                        >
                                            Numbers
                                        </button>
                                        <button
                                            onClick={() => setCustomChars('. , ? \' ! / ( ) & : ; = + - " @')}
                                            className="px-2 py-1.5 text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20 rounded border border-primary/20 transition-colors"
                                        >
                                            Punctuation
                                        </button>
                                        <button
                                            onClick={() => setCustomChars('')}
                                            className="px-2 py-1.5 text-xs font-bold bg-base text-muted hover:text-error hover:bg-base/80 rounded border border-border transition-colors ml-auto"
                                            title="Clear"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted mb-1">Enter characters (space separated optional)</p>
                                        <input
                                            type="text"
                                            value={customChars}
                                            onChange={(e) => setCustomChars(e.target.value)}
                                            placeholder="e.g. K M R S U A P T L O W I"
                                            className="w-full bg-base border border-border rounded p-2 text-main font-mono uppercase focus:border-primary focus:outline-none transition-colors"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 6. Free Form Practice */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'practice' ? 'bg-primary/10 border-primary/50' : inputMode === 'keyboard' ? 'bg-base opacity-50' : 'bg-surface'}`}>
                            <button
                                onClick={() => inputMode !== 'keyboard' && setGameMode('practice')}
                                disabled={inputMode === 'keyboard'}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'practice' ? 'text-primary' : inputMode === 'keyboard' ? 'text-muted cursor-not-allowed' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Edit size={16} /> <span className="font-bold">Free Form Practice</span></div>
                                {gameMode === 'practice' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {inputMode === 'keyboard' && (
                                <div className="p-2 border-t border-border bg-base/50">
                                    <p className="text-[10px] text-muted text-center">⚠️ Not available in Receive Only mode</p>
                                </div>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>

                {/* SECTION 3: MASTERY */}
                <CollapsibleSection
                    title="Mastery"
                    level={3}
                    isCurrentLevel={userSkillLevel === 3}
                    isNextLevel={userSkillLevel === 2}
                    defaultExpanded={userSkillLevel === 3}
                    onExpand={() => setGameMode('callsigns_full')}
                >
                    <div className="space-y-2">
                        {/* Full Callsigns & Codes */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${['callsigns_full', 'qcodes_full', 'othercodes_full'].includes(gameMode) ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('callsigns_full')}
                                className={`w-full p-3 flex items-center justify-between ${['callsigns_full', 'qcodes_full', 'othercodes_full'].includes(gameMode) ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Globe size={16} /> <span className="font-bold">Full Callsigns & Codes</span></div>
                                {['callsigns_full', 'qcodes_full', 'othercodes_full'].includes(gameMode) && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {['callsigns_full', 'qcodes_full', 'othercodes_full'].includes(gameMode) && (
                                <div className="flex flex-col border-t border-primary/20">
                                    <div className="flex divide-x divide-primary/20 border-b border-primary/20">
                                        <button onClick={() => setGameMode('callsigns_full')} className={`flex-1 py-2 text-xs font-bold ${gameMode === 'callsigns_full' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}>Call Signs</button>
                                        <button onClick={() => setGameMode('qcodes_full')} className={`flex-1 py-2 text-xs font-bold ${gameMode === 'qcodes_full' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}>Q-Codes</button>
                                        <button onClick={() => setGameMode('othercodes_full')} className={`flex-1 py-2 text-xs font-bold ${gameMode === 'othercodes_full' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}>Other Codes</button>
                                    </div>
                                    {gameMode === 'callsigns_full' && (
                                        <div className="flex divide-x divide-primary/20">
                                            <button
                                                onClick={() => setCallSignRegion(userSettings.country)}
                                                className={`flex-1 py-2 text-xs font-bold truncate px-2 ${callSignRegion === userSettings.country ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                            >
                                                {COUNTRIES.find(c => c.code === userSettings.country)?.flag} {userSettings.country}
                                            </button>
                                            <button
                                                onClick={() => setCallSignRegion('WORLD')}
                                                className={`flex-1 py-2 text-xs font-bold ${callSignRegion === 'WORLD' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                            >
                                                🌍 World
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Code Quiz */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'code_quiz' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('code_quiz')}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'code_quiz' ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Target size={16} />
                                    <div className="text-left">
                                        <span className="font-bold block">Code Quiz</span>
                                        <span className="text-[10px] text-muted">Learn Q-codes & prosigns</span>
                                    </div>
                                </div>
                                {gameMode === 'code_quiz' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'code_quiz' && (
                                <div className="space-y-1 border-t border-primary/20 p-2">
                                    <div className="text-[10px] text-muted uppercase mb-1 tracking-wider">Input Method</div>
                                    <div className="flex divide-x divide-primary/20 border border-primary/20 rounded overflow-hidden">
                                        <button
                                            onClick={() => setQuizInputMode('keyboard')}
                                            className={`flex-1 py-2 text-xs font-bold ${quizInputMode === 'keyboard' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            <Keyboard size={12} className="inline mr-1" />
                                            Keyboard (50 pts)
                                        </button>
                                        <button
                                            onClick={() => setQuizInputMode('morse')}
                                            className={`flex-1 py-2 text-xs font-bold ${quizInputMode === 'morse' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            <Radio size={12} className="inline mr-1" />
                                            Key Morse (100 pts)
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 7. Simulated QSO */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'qso' ? 'bg-primary/10 border-primary/50' : inputMode === 'keyboard' ? 'bg-base opacity-50' : 'bg-surface'}`}>
                            <button
                                onClick={() => inputMode !== 'keyboard' && setGameMode('qso')}
                                disabled={inputMode === 'keyboard'}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'qso' ? 'text-primary' : inputMode === 'keyboard' ? 'text-muted cursor-not-allowed' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Radio size={16} /> <span className="font-bold">Simulated QSO</span></div>
                                {gameMode === 'qso' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {inputMode === 'keyboard' && (
                                <div className="p-2 border-t border-border bg-base/50">
                                    <p className="text-[10px] text-muted text-center">⚠️ Not available in Receive Only mode</p>
                                </div>
                            )}
                            {gameMode === 'qso' && (
                                <div className="space-y-1 border-t border-primary/20 p-2">
                                    <div className="flex divide-x divide-primary/20 border border-primary/20 rounded overflow-hidden">
                                        <button
                                            onClick={() => setQsoRole('responder')}
                                            className={`flex-1 py-1 text-xs font-bold ${qsoRole === 'responder' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            Answer CQ
                                        </button>
                                        <button
                                            onClick={() => setQsoRole('initiator')}
                                            className={`flex-1 py-1 text-xs font-bold ${qsoRole === 'initiator' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            Call CQ
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 5. Contests */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'contests' ? 'bg-primary/10 border-primary/50' : inputMode === 'keyboard' ? 'bg-base opacity-50' : 'bg-surface'}`}>
                            <button
                                onClick={() => inputMode !== 'keyboard' && setGameMode('contests')}
                                disabled={inputMode === 'keyboard'}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'contests' ? 'text-primary' : inputMode === 'keyboard' ? 'text-muted cursor-not-allowed' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Trophy size={16} /> <span className="font-bold">Contests</span></div>
                                {gameMode === 'contests' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {inputMode === 'keyboard' && (
                                <div className="p-2 border-t border-border bg-base/50">
                                    <p className="text-[10px] text-muted text-center">⚠️ Not available in Receive Only mode</p>
                                </div>
                            )}
                            {gameMode === 'contests' && (
                                <div className="space-y-2 border-t border-primary/20 p-2">
                                    <div className="grid grid-cols-2 gap-1">
                                        <button
                                            onClick={() => setContestType('CQWW_SO')}
                                            className={`py-2 text-xs font-bold rounded ${contestType === 'CQWW_SO' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            🏆 CQ WW DX
                                        </button>
                                        <button
                                            onClick={() => setContestType('CQWPX_CW')}
                                            className={`py-2 text-xs font-bold rounded ${contestType === 'CQWPX_CW' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            📡 CQ WPX
                                        </button>
                                        <button
                                            onClick={() => setContestType('ARRL_SS')}
                                            className={`py-2 text-xs font-bold rounded ${contestType === 'ARRL_SS' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            🧹 ARRL SS
                                        </button>
                                        <button
                                            onClick={() => setContestType('IARU_HF')}
                                            className={`py-2 text-xs font-bold rounded ${contestType === 'IARU_HF' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            🌍 IARU HF
                                        </button>
                                        <button
                                            onClick={() => setContestType('CWT')}
                                            className={`col-span-2 py-2 text-xs font-bold rounded ${contestType === 'CWT' ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                        >
                                            ⚡ CWT (CWOps)
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 8. Pileup Simulator */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'pileup' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('pileup')}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'pileup' ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Radio size={16} /> <span className="font-bold">Pileup Simulator</span></div>
                                {gameMode === 'pileup' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'pileup' && (
                                <div className="space-y-3 border-t border-primary/20 p-3">
                                    <p className="text-xs text-muted leading-relaxed">
                                        <span className="text-primary font-bold">Goal:</span> You are the DX station! Listen to the chaotic mix of callers and pick out <span className="text-main font-bold">ONE</span> valid callsign. Type it to work the station.
                                    </p>

                                    <div className="space-y-1">
                                        <div className="text-[10px] text-muted font-bold uppercase">Number of Stations</div>
                                        <div className="grid grid-cols-4 gap-2">
                                            {[3, 5, 10, 20].map(count => (
                                                <button
                                                    key={count}
                                                    onClick={() => setPileupCount(count)}
                                                    className={`py-1 text-xs font-bold rounded ${pileupCount === count ? 'bg-primary text-primary-content' : 'text-primary hover:bg-primary/20'}`}
                                                >
                                                    {count}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>

                {/* SECTION 4: HEAD COPY */}
                <CollapsibleSection
                    title="Head Copy"
                    level={4}
                    isCurrentLevel={userSkillLevel === 4}
                    isNextLevel={userSkillLevel === 3}
                    defaultExpanded={userSkillLevel === 4}
                    onExpand={() => setGameMode('icr_echo')}
                >
                    <div className="space-y-2">
                        {/* Echo Training */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'icr_echo' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('icr_echo')}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'icr_echo' ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Target size={16} /> <span className="font-bold">Echo Training</span></div>
                                {gameMode === 'icr_echo' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'icr_echo' && (
                                <div className="p-3 border-t border-primary/20">
                                    <p className="text-xs text-muted leading-relaxed">
                                        <span className="text-primary font-bold">Goal:</span> Instant Recognition. Hear the character, then key or type it immediately. Visuals are hidden!
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Common Word Drills */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'icr_words' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('icr_words')}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'icr_words' ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Target size={16} /> <span className="font-bold">Common Word Drills</span></div>
                                {gameMode === 'icr_words' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'icr_words' && (
                                <div className="p-3 border-t border-primary/20">
                                    <p className="text-xs text-muted leading-relaxed">
                                        <span className="text-primary font-bold">Goal:</span> Whole-word recognition. Hear common words and abbreviations, then type them.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Head Copy Practice */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'icr_comprehension' ? 'bg-primary/10 border-primary/50' : 'bg-surface'}`}>
                            <button
                                onClick={() => setGameMode('icr_comprehension')}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'icr_comprehension' ? 'text-primary' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2"><Target size={16} /> <span className="font-bold">Head Copy Practice</span></div>
                                {gameMode === 'icr_comprehension' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold">ACTIVE</div>}
                            </button>
                            {gameMode === 'icr_comprehension' && (
                                <div className="p-3 border-t border-primary/20">
                                    <p className="text-xs text-muted leading-relaxed mb-3">
                                        <span className="text-primary font-bold">Goal:</span> Listen to content without typing, then answer comprehension questions.
                                    </p>

                                    <div className="grid grid-cols-2 gap-2">
                                        {['random', 'wx', 'news', 'literature'].map(mode => (
                                            <button
                                                key={mode}
                                                onClick={() => {
                                                    console.log('[Menu] Selecting sub-mode:', mode);
                                                    setComprehensionMode(mode);
                                                    if (mode === 'literature') {
                                                        setView('literature_library');
                                                    } else {
                                                        startGame('icr_comprehension');
                                                    }
                                                }}
                                                className={`px-2 py-1.5 rounded text-xs font-bold border transition-all relative ${comprehensionMode === mode
                                                    ? 'bg-primary text-primary-content border-primary shadow-[0_0_10px_rgba(var(--color-primary-rgb),0.3)]'
                                                    : 'bg-base text-muted border-border hover:border-primary/50 hover:text-primary'
                                                    } ${mode === 'random' ? 'col-span-2' : ''}`}
                                            >
                                                {mode === 'wx' ? 'Local WX' : mode === 'news' ? 'Daily News' : mode === 'literature' ? 'Poetry & Stories' : 'Random Mix'}
                                                {mode === 'literature' && (
                                                    <span className="absolute -top-1 -right-1 bg-primary text-primary-content text-[7px] font-black px-1 py-0.5 rounded shadow-lg transform rotate-12 scale-90 animate-pulse">
                                                        NEW
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>
                {/* SECTION 5: FIELD OPERATIONS */}
                <CollapsibleSection
                    title="Field Operations"
                    level={5}
                    isCurrentLevel={userSkillLevel === 5}
                    isNextLevel={userSkillLevel === 4}
                    defaultExpanded={userSkillLevel === 5}
                    onExpand={() => setGameMode('pro_qso')}
                >
                    <div className="space-y-2">
                        {/* 9. Pro-Grade Exchange */}
                        <div className={`rounded-lg border border-border overflow-hidden transition-all ${gameMode === 'pro_qso' ? 'bg-primary/10 border-primary/50' : inputMode === 'keyboard' ? 'bg-base opacity-50' : 'bg-surface'}`}>
                            <button
                                onClick={() => inputMode !== 'keyboard' && setGameMode('pro_qso')}
                                disabled={inputMode === 'keyboard'}
                                className={`w-full p-3 flex items-center justify-between ${gameMode === 'pro_qso' ? 'text-primary' : inputMode === 'keyboard' ? 'text-muted cursor-not-allowed' : 'text-muted'}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Radio size={16} />
                                    <div className="text-left">
                                        <span className="font-bold block text-sm">Pro-Grade Exchange</span>
                                        <span className="text-[10px] text-muted">Atmospheric Noise • QSO 2.0</span>
                                    </div>
                                </div>
                                {gameMode === 'pro_qso' && <div className="text-xs bg-primary px-2 py-1 rounded text-primary-content font-bold shadow-pulse">LIVE</div>}
                            </button>
                            {inputMode === 'keyboard' && (
                                <div className="p-2 border-t border-border bg-base/50">
                                    <p className="text-[10px] text-muted text-center">⚠️ Not available in Receive Only mode</p>
                                </div>
                            )}
                            {gameMode === 'pro_qso' && (
                                <div className="p-3 border-t border-primary/20 space-y-3">
                                    <p className="text-xs text-muted leading-relaxed">
                                        <span className="text-primary font-bold">QSO 2.0:</span> Realistic stateful contacts. Extract signals through static, fading (QSB), and frequency drift.
                                        Commands like <span className="text-main font-mono">?</span> or <span className="text-main font-mono">QRS</span> are supported.
                                    </p>
                                    <div className="bg-base/40 rounded-lg p-2 border border-border/50 text-center">
                                        <span className="text-[10px] uppercase font-bold text-primary/70 animate-pulse">Advanced Experience</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>
            </div>
        </>

    );
};

export default MenuGameModes;
